import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, CheckCircle, User, Mail, Phone, MessageSquare, Loader, Clock } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { submitOrder } from '../../services/orders.service';
import { ContactPreference, OrderPayload, TimeWindow } from '../../types';
import toast from 'react-hot-toast';

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

export function CheckoutForm({ onBack, onSuccess }: Props) {
  const { state, clearCart } = useCart();
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [contactPref, setContactPref] = useState<ContactPreference>('call');
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('any');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [orderMsg, setOrderMsg] = useState('');

  function validate() {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) errs.email = 'Invalid email address';
    if (!form.phone.trim()) errs.phone = 'Phone number is required';
    else if (!/^\+?[\d\s\-()]{7,20}$/.test(form.phone)) errs.phone = 'Invalid phone number';
    return errs;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);

    const payload: OrderPayload = {
      customer: { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), notes: form.notes.trim() || undefined },
      fulfillment: {
        method: 'pickup',
        contactPreference: contactPref,
        timeWindow,
      },
      items: state.items.map((item) => ({
        productId: item.product.productId,
        productName: item.product.name,
        brand: item.product.brand,
        variantId: item.variant.variantId,
        variantDescription: (item.variant as any).variantDescription || [item.variant.color, item.variant.size, item.variant.compressionLevel ? `${item.variant.compressionLevel} mmHg` : null].filter(Boolean).join(' / '),
        quantity: item.quantity,
        imageUrl: item.product.imageUrl,
        preorder: item.variant.stock?.status === 'preorder',
        color: item.variant.color || undefined,
        compressionLevel: item.variant.compressionLevel || undefined,
        size: item.variant.size || undefined,
      })),
    };

    try {
      const res = await submitOrder(payload);
      setOrderId(res.orderId);
      setOrderMsg(res.message);
      // Remember for the Track Order form so it can prefill next time
      try {
        localStorage.setItem('ndms-last-order', JSON.stringify({ orderId: res.orderId, email: form.email.trim() }));
      } catch {}
      setSuccess(true);
      clearCart();
      toast.success('Order submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit order. Please call us directly.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 10, stiffness: 200, delay: 0.1 }}
          className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-5"
        >
          <CheckCircle size={40} className="text-green-500" />
        </motion.div>
        <h3 className="font-bold text-ndms-navy text-xl mb-2">Order Received!</h3>
        <p className="text-gray-600 text-sm mb-2">
          Thank you, <strong>{form.name}</strong>! Your order has been submitted.
        </p>
        <p className="text-gray-500 text-xs mb-1">Order ID: <span className="font-mono font-semibold">{orderId}</span></p>
        <p className="text-gray-500 text-sm mb-6">
          {orderMsg || 'Our team will prepare your order and let you know when it’s ready.'}
        </p>
        <button onClick={onSuccess} className="btn-primary px-8 py-3 rounded-xl">
          Done
        </button>
      </motion.div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h3 className="font-bold text-ndms-navy">Your Contact Info</h3>
          <p className="text-xs text-gray-400">No payment now — our team will contact you</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto scrollbar-thin px-5 py-5 space-y-4">
        {/* Order summary */}
        <div className="bg-ndms-cream rounded-xl p-3">
          <p className="text-xs font-semibold text-ndms-navy mb-2">Order Summary ({state.items.length} items)</p>
          <div className="space-y-1">
            {state.items.map((item) => (
              <div key={item.cartItemId} className="flex justify-between text-xs text-gray-600">
                <span className="truncate mr-2">{item.product.name} ×{item.quantity}</span>
                {item.variant.priceInCents && (
                  <span className="font-medium flex-shrink-0">${((item.variant.priceInCents * item.quantity) / 100).toFixed(2)}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Fields */}
        {[
          { field: 'name', label: 'Full Name', icon: User, type: 'text', placeholder: 'Jane Smith', maxLen: 200, auto: 'name' },
          { field: 'email', label: 'Email Address', icon: Mail, type: 'email', placeholder: 'jane@example.com', maxLen: 254, auto: 'email' },
          { field: 'phone', label: 'Phone Number', icon: Phone, type: 'tel', placeholder: '+1 (555) 000-0000', maxLen: 30, auto: 'tel' },
        ].map(({ field, label, icon: Icon, type, placeholder, maxLen, auto }) => (
          <div key={field}>
            <label className="block text-sm font-semibold text-ndms-navy mb-1.5">{label} *</label>
            <div className="relative">
              <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={type}
                value={form[field as keyof typeof form]}
                onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                placeholder={placeholder}
                maxLength={maxLen}
                autoComplete={auto}
                className={`input-base pl-10 ${errors[field] ? 'border-red-400 focus:ring-red-400' : ''}`}
              />
            </div>
            {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
          </div>
        ))}

        {/* Pickup notice — pickup is the only fulfillment method */}
        <div className="flex items-center gap-2 bg-ndms-cream rounded-xl px-3.5 py-2.5">
          <Clock size={14} className="text-ndms-blue flex-shrink-0" />
          <p className="text-xs text-gray-600">
            <span className="font-semibold text-ndms-navy">Pickup at the store.</span> We'll email you when your order is ready.
          </p>
        </div>

        {/* Contact preference */}
        <div>
          <label className="block text-sm font-semibold text-ndms-navy mb-1.5">How should we contact you?</label>
          <div className="flex gap-2">
            {([
              { v: 'call' as const, label: 'Phone Call', icon: Phone },
              { v: 'text' as const, label: 'Text', icon: MessageSquare },
              { v: 'email' as const, label: 'Email', icon: Mail },
            ]).map(({ v, label, icon: Icon }) => (
              <button
                key={v}
                type="button"
                onClick={() => setContactPref(v)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                  contactPref === v
                    ? 'border-ndms-navy bg-ndms-navy text-white'
                    : 'border-gray-200 text-gray-600 hover:border-ndms-navy'
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* Preferred time window */}
        <div>
          <label className="block text-sm font-semibold text-ndms-navy mb-1.5 flex items-center gap-1.5">
            <Clock size={13} /> Best time for pickup
          </label>
          <div className="flex flex-wrap gap-1.5">
            {([
              { v: 'any' as const, label: 'Anytime' },
              { v: 'morning' as const, label: 'Morning' },
              { v: 'afternoon' as const, label: 'Afternoon' },
              { v: 'evening' as const, label: 'Evening' },
            ]).map(({ v, label }) => (
              <button
                key={v}
                type="button"
                onClick={() => setTimeWindow(v)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 transition-all ${
                  timeWindow === v
                    ? 'border-ndms-navy bg-ndms-navy text-white'
                    : 'border-gray-200 text-gray-600 hover:border-ndms-navy'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-semibold text-ndms-navy mb-1.5">
            Special Instructions <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <div className="relative">
            <MessageSquare size={16} className="absolute left-3.5 top-3.5 text-gray-400" />
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Any questions, fitting concerns, or special requirements..."
              rows={3}
              maxLength={1000}
              className="input-base pl-10 resize-none"
            />
          </div>
        </div>

        <p className="text-xs text-gray-400 leading-relaxed">
          By submitting this order, you agree to be contacted by our staff to confirm availability and arrange payment/pickup. No charges are processed online.
        </p>
      </form>

      {/* Submit footer */}
      <div className="border-t border-gray-100 px-5 py-4">
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={handleSubmit}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-ndms-navy text-white font-bold rounded-2xl hover:bg-ndms-navy-light transition-all shadow-lg hover:shadow-xl disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader size={18} className="animate-spin" />
              Submitting Order...
            </>
          ) : (
            <>
              <Send size={18} />
              Submit Order Request
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
}
